import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/i18n";
import { useSeoMeta } from "@/lib/useSeoMeta";
import { orgAndWebSite, breadcrumbFromPath, formatUpdatedFR, CONTENT_LAST_MODIFIED, DIRECTIVE_APPLICABLE_SINCE, ORG_ID, WEBSITE_ID } from "@/lib/schemaOrg";

/**
 * Page /a-propos — entité "EmpCo Compliance".
 * Objectif GEO : donner aux assistants IA (Le Chat, Perplexity, ChatGPT…) une page canonique
 * "Qu'est-ce que EmpCo Compliance ?" avec méthode, limites, responsable éditorial.
 * Les placeholders [À COMPLÉTER] sont visibles côté code — à renseigner par l'éditeur.
 */
export default function AboutPage() {
  const { lang } = useLang();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const canonical = lang === "fr" ? "https://empco-compliance.eu/a-propos" : `https://empco-compliance.eu/${lang}/a-propos`;

  const org = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    url: canonical,
    inLanguage: lang,
    isPartOf: { "@id": WEBSITE_ID },
    mainEntity: { "@id": ORG_ID },
  };

  useSeoMeta({
    title: "À propos d'EmpCo Compliance | EmpCo Compliance",
    description: "EmpCo Compliance est un logiciel SaaS édité par EmpCo SAS (France) qui aide les marques à documenter leurs allégations environnementales au regard de la directive (UE) 2024/825.",
    canonical,
    jsonLd: [orgAndWebSite(lang), org, breadcrumbFromPath("/a-propos", "À propos", lang)],
  });

  return (
    <div className="min-h-screen bg-background" data-testid="AboutPage">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-16 prose prose-slate">
        <p className="eyebrow mb-2">À propos</p>
        <h1 className="font-serif text-4xl tracking-tight mb-6">EmpCo Compliance, un outil d'analyse d'allégations environnementales</h1>
        <p className="text-lg text-muted-foreground">Mis à jour le {formatUpdatedFR(CONTENT_LAST_MODIFIED)} · Applicable depuis le {formatUpdatedFR(DIRECTIVE_APPLICABLE_SINCE)}</p>

        <h2 className="mt-10">Qui édite l'outil</h2>
        <p>
          EmpCo Compliance est un logiciel SaaS édité par <strong>EmpCo SAS</strong>, société par actions simplifiée
          immatriculée en France sous le SIREN <code>[SIREN À COMPLÉTER]</code>, dont le siège social est situé
          <code> [ADRESSE À COMPLÉTER]</code>. L'entreprise a été créée en 2025 pour outiller les marques B2C
          européennes face à la directive (UE) 2024/825 sur l'autonomisation des consommateurs pour la transition
          écologique.
        </p>

        <h2 className="mt-10">Ce que fait l'outil (et ce qu'il ne fait pas)</h2>
        <ul>
          <li>Scanne un site internet, une image ou un document.</li>
          <li>Détecte les termes environnementaux susceptibles d'être qualifiés d'allégations au sens de la directive.</li>
          <li>Qualifie chaque allégation par IA en s'appuyant sur un corpus versionné de règles, puis suggère une reformulation.</li>
          <li>Historise chaque version d'allégation et associe les preuves attachées.</li>
          <li>Génère un dossier PDF horodaté prêt à être produit en cas de contrôle DGCCRF.</li>
        </ul>
        <p><strong>Limites explicites :</strong> obligation de moyens, pas de résultat. L'outil ne délivre aucun badge,
          aucun label, aucun certificat de conformité et n'émet aucun verdict juridique. Les résultats sont générés
          automatiquement et doivent être relus par une personne compétente (responsable conformité, juriste ou avocat).
          EmpCo Compliance ne constitue pas un conseil juridique.</p>

        <h2 className="mt-10">Méthode d'analyse</h2>
        <ol>
          <li><strong>Crawl</strong> — parcours des pages publiques du site ou lecture du fichier fourni.</li>
          <li><strong>Préfiltre lexical versionné</strong> — repérage des termes du corpus interne.</li>
          <li><strong>Qualification IA</strong> — chaque item passe par un modèle de langage qui apprécie le contexte
            (une « pomme verte » n'est pas une allégation environnementale) et propose un verdict de travail.</li>
          <li><strong>Registre vivant</strong> — chaque allégation reçoit un identifiant unique, une version, un
            emplacement et un statut.</li>
          <li><strong>Alertes</strong> — expirations de preuves (J-90 / J-30 / J-7) et changements d'allégation.</li>
          <li><strong>Dossier de contrôle</strong> — PDF horodaté regroupant l'analyse, les preuves et le journal d'audit.</li>
        </ol>

        <h2 className="mt-10">Responsable éditorial</h2>
        <p>
          Le contenu réglementaire d'empco-compliance.eu est produit sous la responsabilité de
          <strong> Nicolas Philizor</strong>, Fondateur et responsable éditorial d'EmpCo Compliance
          (<a href="https://www.linkedin.com/in/nicolas-philizor-0344a7206/" target="_blank" rel="noopener noreferrer">profil LinkedIn</a>).
          Toute question sur une page ou une source citée peut être adressée à
          <a href="mailto:contact@empco-compliance.eu"> contact@empco-compliance.eu</a>.
        </p>

        <h2 className="mt-10">Nous suivre</h2>
        <ul>
          <li><a href="https://www.linkedin.com/company/146606902/" target="_blank" rel="noopener noreferrer">EmpCo Compliance sur LinkedIn</a> — actualités réglementaires, décisions DGCCRF, retours d'audit.</li>
          <li><a href="https://www.youtube.com/@EMPCOCOMPLIANCE" target="_blank" rel="noopener noreferrer">Chaîne YouTube EmpCo Compliance</a> — décryptages de la directive UE 2024/825 et cas pratiques.</li>
        </ul>

        <h2 className="mt-10">Textes et sources de référence</h2>
        <ul>
          <li><a href="https://eur-lex.europa.eu/eli/dir/2024/825/oj" target="_blank" rel="noopener noreferrer">Directive (UE) 2024/825 sur EUR-Lex</a></li>
          <li><a href="https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX%3A32005L0029" target="_blank" rel="noopener noreferrer">Directive 2005/29/CE sur EUR-Lex</a></li>
          <li><a href="https://www.legifrance.gouv.fr/codes/section_lc/LEGITEXT000006069565/LEGISCTA000032227103/" target="_blank" rel="noopener noreferrer">Code de la consommation, articles L.121-1 et suivants — Légifrance</a></li>
          <li><a href="https://www.economie.gouv.fr/dgccrf" target="_blank" rel="noopener noreferrer">DGCCRF — Direction générale de la concurrence, de la consommation et de la répression des fraudes</a></li>
        </ul>
      </main>
      <Footer />
    </div>
  );
}
